import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";

const DISCLAIMER =
  "This policy governs refund eligibility for Regulens subscriptions and one-time charges. Regulens is not a law firm and this policy does not constitute legal advice.";

const SUPPORT_EMAIL = "support@getregulens.com";

const Section = ({ title, children, testId }) => (
  <section data-testid={testId}>
    <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">{title}</h2>
    {children}
  </section>
);

export default function RefundPolicy() {
  return (
    <Layout>
      <section
        className="max-w-3xl mx-auto px-6 py-12"
        data-testid="refund-policy-page"
      >
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">
          Legal · Billing
        </div>
        <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-4">
          Refund Policy
        </h1>

        <div
          className="mb-8 rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-4 text-sm text-[var(--rg-amber)]"
          data-testid="refund-disclaimer"
        >
          {DISCLAIMER}
        </div>

        <article className="prose-invert space-y-6 text-[var(--rg-text)] leading-relaxed">
          <Section title="Cancellations" testId="refund-section-cancel">
            <p className="text-sm">
              Subscriptions can be cancelled at any time from your account
              billing settings. Cancellations take effect at the end of the
              current billing period — paid time already on your account is
              honoured to the end of that period. <strong>No mid-period
              refunds are issued for cancellations.</strong>
            </p>
          </Section>

          <Section title="Refund eligibility window" testId="refund-section-window">
            <p className="text-sm">
              Refund requests are only considered <strong>within 7 days of the
              initial purchase</strong>, and only for genuine technical
              failures where the readiness audit could not be completed due to
              a fault on Regulens&apos; side.
            </p>
          </Section>

          <Section title="What is NOT eligible for a refund" testId="refund-section-not-eligible">
            <ul className="list-disc pl-6 space-y-1 text-sm">
              <li>Change of mind after purchase.</li>
              <li>Partial use of subscription features.</li>
              <li>
                Dissatisfaction with readiness score results. Scores are
                informational only and are <strong>not</strong> guaranteed
                outcomes, certifications, or legal-compliance verdicts.
              </li>
              <li>Failure to use the platform after subscribing.</li>
            </ul>
          </Section>

          <Section title="How to submit a refund request" testId="refund-section-how">
            <p className="text-sm">
              Refund requests must be submitted via email to{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[var(--rg-sage)] hover:underline"
                data-testid="refund-contact-email"
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              with <strong>verifiable order details</strong> — including the
              order ID, account email, and date of purchase.{" "}
              <strong>
                Requests submitted without sufficient order information will
                not be processed.
              </strong>
            </p>
          </Section>

          <Section title="Fraud and abuse" testId="refund-section-fraud">
            <p className="text-sm">
              Regulens reserves the right to deny any refund request that
              appears fraudulent or abusive, including but not limited to
              chargeback abuse, repeated cancel-and-refund cycles, and refund
              requests followed by continued use of derived audit data.
            </p>
          </Section>

          <Section title="One-refund limit" testId="refund-section-limit">
            <p className="text-sm">
              A maximum of <strong>one (1) refund per customer account, ever</strong>.
              Subsequent eligibility windows on additional purchases are
              automatically void for accounts that have received a prior refund.
            </p>
          </Section>

          <Section title="Currency, fees, and exchange rates" testId="refund-section-currency">
            <p className="text-sm">
              All refunds are issued in the <strong>exact same currency in
              which the original payment was captured</strong>. Regulens is
              not responsible for any exchange-rate differences, foreign-
              transaction fees, or international processing fees applied by
              the customer&apos;s bank or card issuer. If your card was charged in
              a currency other than your account-of-record currency, the
              refund will be returned in the original charge currency.
            </p>
          </Section>

          <Section title="Processing time" testId="refund-section-time">
            <p className="text-sm">
              Approved refunds are typically returned within 5&ndash;10 business
              days. Settlement times beyond that are subject to your card
              issuer&apos;s policies and are outside Regulens&apos; control.
            </p>
          </Section>

          <Section title="Related" testId="refund-section-related">
            <p className="text-sm">
              See also our{" "}
              <Link to="/terms-of-service" className="text-[var(--rg-sage)] hover:underline" data-testid="refund-link-terms">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy-policy" className="text-[var(--rg-sage)] hover:underline" data-testid="refund-link-privacy">
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <p className="text-xs text-[var(--rg-muted)] italic mt-8">
            This policy was last updated June 2026.
          </p>
        </article>
      </section>
    </Layout>
  );
}
